// @figma file=tbd node=tbd name="AnalyticsDashboard" set="araf-analytics" verified=tbd
import * as React from 'react';
import { cn } from '../lib/utils';
import { ActiveSessionsTable } from './active-sessions-table';
import { AuditLogTable } from './audit-log-table';
import { SessionStatsCard } from './session-stats-card';
import type { ActiveSessionDto, AuditLogRecordDto } from './types';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AnalyticsDashboardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Tenant scope. Pass undefined for cross-tenant admin view.
   */
  tenantSlug?: string;

  // Session stats
  activeSessionCount: number;
  sessionsLoading?: boolean;

  // Active sessions table
  activeSessions: ActiveSessionDto[];
  activeSessionsLoading?: boolean;

  // Audit log table
  auditRecords: AuditLogRecordDto[];
  auditTotal: number;
  auditPage: number;
  auditPageSize: number;
  onAuditPageChange: (page: number) => void;
  auditLoading?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AnalyticsDashboard = React.forwardRef<HTMLDivElement, AnalyticsDashboardProps>(
  (
    {
      className,
      tenantSlug,
      activeSessionCount,
      sessionsLoading = false,
      activeSessions,
      activeSessionsLoading = false,
      auditRecords,
      auditTotal,
      auditPage,
      auditPageSize,
      onAuditPageChange,
      auditLoading = false,
      ...props
    },
    ref,
  ) => {
    return (
      <div ref={ref} className={cn('flex flex-col gap-6', className)} {...props}>
        {/* Stats row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SessionStatsCard
            count={activeSessionCount}
            tenantSlug={tenantSlug}
            isLoading={sessionsLoading}
          />
        </div>

        {/* Active sessions */}
        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-foreground">Active sessions</h2>
          <ActiveSessionsTable
            sessions={activeSessions}
            tenantSlug={tenantSlug}
            loading={activeSessionsLoading}
          />
        </section>

        {/* Audit log */}
        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-foreground">Audit log</h2>
          <AuditLogTable
            records={auditRecords}
            total={auditTotal}
            page={auditPage}
            pageSize={auditPageSize}
            onPageChange={onAuditPageChange}
            tenantSlug={tenantSlug}
            loading={auditLoading}
          />
        </section>
      </div>
    );
  },
);
AnalyticsDashboard.displayName = 'AnalyticsDashboard';

export { AnalyticsDashboard };
